// src/pages/HomePage.jsx

import {
    Box,
    Button,
    Container,
    Group,
    Paper,
    Stack,
    Text,
    Title,
    Grid,
    Badge,
    SimpleGrid,
    Image,
    List,
    ThemeIcon,
} from "@mantine/core";

export default function HomePage() {
    const problems = [
        "Manual paperwork and customer registration",
        "Physical document collection",
        "Time-consuming booking management",
        "Confusion during vehicle handover and return",
        "Damage disputes without proper proof",
        "Unorganized customer and vehicle tracking",
    ];

    const howItWorks = [
        {
            step: "01",
            title: "Owner Registers Rental Shop",
            desc: "The rental owner adds shop details, vehicle categories, rental information, and fleet details.",
        },
        {
            step: "02",
            title: "RentalFlow Generates QR Code",
            desc: "A unique QR code is created for the shop. The owner can print it or share it digitally.",
        },
        {
            step: "03",
            title: "Customer Scans QR",
            desc: "The customer scans the shop QR code and opens the booking request form on their mobile device.",
        },
        {
            step: "04",
            title: "Customer Uploads Details",
            desc: "The customer submits personal details, Aadhaar, Driving Licence, pickup time, and return time.",
        },
        {
            step: "05",
            title: "Owner Verifies & Assigns Vehicle",
            desc: "The owner checks customer documents, verifies the request, and assigns a vehicle.",
        },
        {
            step: "06",
            title: "Pre-Ride Photos Are Uploaded",
            desc: "Before handover, the owner uploads vehicle photos as proof of condition.",
        },
        {
            step: "07",
            title: "Vehicle Return Becomes Clear",
            desc: "During return, the owner can compare the condition and avoid unnecessary disputes.",
        },
    ];

    const pricingPlans = [
        {
            title: "1 Month",
            price: "₹499",
            desc: "Best for trying RentalFlow for your rental business.",
            badge: "Starter",
        },
        {
            title: "6 Months",
            price: "₹2,499",
            desc: "Good for shops that want to use RentalFlow for a longer period.",
            badge: "Popular",
        },
        {
            title: "12 Months",
            price: "₹3,999",
            desc: "Best value for rental shops that want yearly access.",
            badge: "Best Value",
        },
    ];

    const oneTimePlan = {
        title: "One-Time Fee",
        price: "₹9,999",
        desc: "Limited-time offer for rental owners who want long-term access without monthly renewal.",
        badge: "Limited Time",
    };

    return (
       <Box
    style={{
        minHeight: "100vh",
        backgroundColor: "#f0ede8",
    }}
>
    <Box
        style={{
            background:
                "radial-gradient(circle at top left, rgba(242,76,26,0.25), transparent 35%), linear-gradient(135deg, #1a1a1a 0%, #090909 100%)",
            padding: "28px 20px 90px",
        }}
    >
        <Container size="lg">
            <Group justify="flex-end" mb={36}>
                <Button
                    component="a"
                    href="https://play.google.com/store/apps/details?id=in.rentflow.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="md"
                    radius={14}
                    style={{
                        backgroundColor: "#34a853",
                        fontWeight: 700,
                    }}
                >
                    ▶ Get it on Google Play
                </Button>
            </Group>

            <Grid align="center" gutter={48}>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap={18}>
                        <Title
                            order={1}
                            c="white"
                            fw={900}
                            style={{
                                fontSize: "clamp(42px, 6vw, 72px)",
                                lineHeight: 1.05,
                            }}
                        >
                            RentalFlow
                        </Title>
                                <Badge
                                    size="lg"
                                    radius="xl"
                                    color="orange"
                                    variant="light"
                                    w="fit-content"
                                >
                                    QR-Based Vehicle Rental Platform
                                </Badge>
                                <Text
                                    c="#d0d0d0"
                                    size="xl"
                                    style={{ lineHeight: 1.7 }}
                                >
                                    Simplifying vehicle rentals with QR-based
                                    booking, digital document collection,
                                    customer verification, fleet management,
                                    vehicle assignment, and pre-ride photo proof.
                                </Text>

                                <Group mt={10}>
                                    <Button
                                        component="a"
                                        href="#how-it-works"
                                        size="md"
                                        radius={14}
                                        style={{
                                            backgroundColor: "#f24c1a",
                                            fontWeight: 700,
                                        }}
                                    >
                                        See How It Works
                                    </Button>

                                    <Button
                                        component="a"
                                        href="/product"
                                        size="md"
                                        radius={14}
                                        variant="outline"
                                        c="white"
                                        style={{
                                            borderColor: "#555",
                                        }}
                                    >
                                        View Features
                                    </Button>
                                </Group>
                            </Stack>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Paper
                                radius={28}
                                p={18}
                                bg="#161616"
                                style={{
                                    border: "1px solid #2d2d2d",
                                    boxShadow:
                                        "0 30px 80px rgba(0,0,0,0.35)",
                                }}
                            >
                                <Image
                                    src="/rentalflow/RentalFlowHero.png"
                                    fallbackSrc="https://placehold.co/900x650/1b1b1b/f24c1a?text=Customer+Renting+Vehicle"
                                    alt="Customer renting a vehicle using RentalFlow QR booking"
                                    radius={20}
                                />
                            </Paper>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            {/* Problem Section */}
            <Container size="lg" py={80}>
                <Grid align="center" gutter={48}>
                    <Grid.Col span={{ base: 12, md: 5 }}>
                        <Stack gap={14}>
                            <Badge color="dark" variant="light" w="fit-content">
                                The Problem
                            </Badge>

                            <Title order={2} fw={900} c="#1a1a1a">
                                Vehicle rental shops still depend on manual work
                            </Title>

                            <Text c="#666" size="md" style={{ lineHeight: 1.8 }}>
                                Rental owners spend too much time collecting
                                documents, managing bookings, assigning vehicles,
                                and handling vehicle damage disputes manually.
                            </Text>
                        </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 7 }}>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={18}>
                            {problems.map((problem) => (
                                <Paper
                                    key={problem}
                                    radius={18}
                                    p={22}
                                    bg="white"
                                    shadow="xs"
                                    style={{
                                        border: "1px solid #e7e2db",
                                    }}
                                >
                                    <Group align="flex-start" gap={12}>
                                        {/* <ThemeIcon
                                            radius="xl"
                                            size={34}
                                            color="orange"
                                            variant="light"
                                        >
                                            ✓
                                        </ThemeIcon> */}

                                        <Text fw={700} c="#222">
                                            {problem}
                                        </Text>
                                    </Group>
                                </Paper>
                            ))}
                        </SimpleGrid>
                    </Grid.Col>
                </Grid>
            </Container>

            {/* About Section */}
            <Box
                py={80}
                style={{
                    backgroundColor: "#f7f4ef",
                    borderTop: "1px solid #e7e2db",
                    borderBottom: "1px solid #e7e2db",
                }}
            >
                <Container size="lg">
                    <Stack align="center" mb={48}>
                        <Badge color="orange" variant="light">
                            About RentalFlow
                        </Badge>

                        <Title order={2} fw={900} c="#1a1a1a" ta="center">
                            One digital flow for the complete rental journey
                        </Title>

                        <Text
                            ta="center"
                            c="#666"
                            maw={760}
                            size="md"
                            style={{ lineHeight: 1.8 }}
                        >
                            RentalFlow is built for bike and car rental
                            businesses. Once the rental shop is registered, the
                            owner gets a unique QR code. Customers scan the QR,
                            submit their booking request, upload documents, and
                            the owner manages everything digitally.
                        </Text>
                    </Stack>

                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={24}>
                        {[
                            "QR-based customer booking",
                            "Digital Aadhaar and Driving Licence upload",
                            "Booking request management",
                            "Customer document verification",
                            "Vehicle and fleet management",
                            "Pre-ride vehicle photo proof",
                        ].map((item) => (
                            <Paper
                                key={item}
                                radius={22}
                                p={24}
                                bg="white"
                                shadow="xs"
                                style={{
                                    border: "1px solid #ece7df",
                                }}
                            >
                                <Text fw={800} c="#1a1a1a">
                                    {item}
                                </Text>
                            </Paper>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Features With Images */}
            {/* 
            <Box id="features" py={80} style={{ backgroundColor: "#111" }}>
                <Container size="lg">
                    <Stack align="center" mb={50}>
                        <Badge color="orange" variant="light">
                            Product Screens
                        </Badge>

                        <Title order={2} fw={900} c="white" ta="center">
                            Everything your rental shop needs
                        </Title>

                        <Text ta="center" c="#bbb" maw={720} style={{ lineHeight: 1.8 }}>
                            These sections use your actual screenshots, so
                            visitors can understand the product without reading
                            a separate document.
                        </Text>
                    </Stack>

                    <Stack gap={28}>
                        {features.map((item, index) => (
                            <Paper
                                key={item.title}
                                radius={28}
                                p={{ base: 20, md: 28 }}
                                bg="#1b1b1b"
                                style={{
                                    border: "1px solid #2a2a2a",
                                }}
                            >
                                <Grid align="center" gutter={30}>
                                   
                                    <Grid.Col
                                        span={{ base: 12, md: 6 }}
                                        order={{ base: 2, md: index % 2 === 0 ? 1 : 2 }}
                                    >
                                        <Image
                                            src={item.image}
                                            fallbackSrc={`https://placehold.co/900x650/222222/f24c1a?text=${encodeURIComponent(
                                                item.title
                                            )}`}
                                            alt={item.title}
                                            radius={22}
                                        />
                                    </Grid.Col>

                                  
                                    <Grid.Col
                                        span={{ base: 12, md: 6 }}
                                        order={{ base: 1, md: index % 2 === 0 ? 2 : 1 }}
                                    >
                                        <Stack gap={14}>
                                            <Badge
                                                color="orange"
                                                variant="filled"
                                                radius="xl"
                                                w="fit-content"
                                            >
                                                {String(index + 1).padStart(2, "0")}
                                            </Badge>

                                            <Title order={3} c="white" fw={900}>
                                                {item.title}
                                            </Title>

                                            <Text c="#bdbdbd" size="md" style={{ lineHeight: 1.8 }}>
                                                {item.desc}
                                            </Text>
                                        </Stack>
                                    </Grid.Col>
                                </Grid>
                            </Paper>
                        ))}
                    </Stack>
                </Container>
            </Box>
 */}
            {/* How It Works */}
            <Container id="how-it-works" size="lg" py={80}>
                <Stack align="center" mb={48}>
                    <Badge color="orange" variant="light">
                        How It Works
                    </Badge>

                    <Title order={2} fw={900} c="#1a1a1a" ta="center">
                        From QR scan to vehicle return
                    </Title>

                    <Text ta="center" c="#666" maw={720} style={{ lineHeight: 1.8 }}>
                        RentalFlow creates a simple, organized process for both
                        the customer and the rental owner.
                    </Text>
                </Stack>

                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={22}>
                    {howItWorks.map((item) => (
                        <Paper
                            key={item.step}
                            radius={22}
                            p={26}
                            bg="white"
                            shadow="xs"
                            style={{
                                border: "1px solid #ece7df",
                                height: "100%",
                            }}
                        >
                            <Stack gap={12}>
                                <Badge
                                    color="orange"
                                    variant="filled"
                                    radius="xl"
                                    w="fit-content"
                                >
                                    Step {item.step}
                                </Badge>

                                <Title order={4} c="#1a1a1a">
                                    {item.title}
                                </Title>

                                <Text c="#666" size="sm" style={{ lineHeight: 1.7 }}>
                                    {item.desc}
                                </Text>
                            </Stack>
                        </Paper>
                    ))}
                </SimpleGrid>
            </Container>

            {/* Pre-Ride Proof Section */}
            <Box
                py={80}
                style={{
                    backgroundColor: "#f7f4ef",
                    borderTop: "1px solid #e7e2db",
                    borderBottom: "1px solid #e7e2db",
                }}
            >
                <Container size="lg">
                    <Grid align="center" gutter={48}>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Stack gap={16}>
                                <Badge color="orange" variant="light" w="fit-content">
                                    Damage Dispute Protection
                                </Badge>

                                <Title order={2} fw={900} c="#1a1a1a">
                                    Vehicle condition proof before every ride
                                </Title>

                                <Text c="#666" size="md" style={{ lineHeight: 1.8 }}>
                                    Before handing over the vehicle, the owner
                                    can upload photos of the vehicle condition.
                                    This creates a clear record of scratches,
                                    dents, or existing damage before the ride
                                    starts.
                                </Text>

                                <List spacing="sm" c="#555">
                                    <List.Item>Proof of vehicle condition before rental</List.Item>
                                    <List.Item>Digital record of scratches or existing damage</List.Item>
                                    <List.Item>Reference during vehicle return</List.Item>
                                    <List.Item>Helps reduce damage disputes</List.Item>
                                </List>
                            </Stack>
                        </Grid.Col>

                    </Grid>
                </Container>
            </Box>

            {/* Pricing Section */}
            <Box
                id="pricing"
                py={80}
                style={{
                    backgroundColor: "#111",
                }}
            >
                <Container size="lg">
                    <Stack align="center" mb={50}>
                        <Badge color="orange" variant="light">
                            Subscription Plans
                        </Badge>

                        <Title order={2} fw={900} c="white" ta="center">
                            Simple pricing for rental shops
                        </Title>

                        <Text
                            ta="center"
                            c="#bbb"
                            maw={720}
                            style={{ lineHeight: 1.8 }}
                        >
                            Choose a plan based on how long you want to use RentalFlow for
                            your bike or car rental business.
                        </Text>
                    </Stack>

                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={24}>
                        {pricingPlans.map((plan) => (
                            <Paper
                                key={plan.title}
                                radius={26}
                                p={30}
                                bg="#1b1b1b"
                                style={{
                                    border:
                                        plan.badge === "Popular"
                                            ? "1px solid #f24c1a"
                                            : "1px solid #2a2a2a",
                                    height: "100%",
                                }}
                            >
                                <Stack gap={14}>
                                    <Badge
                                        color="orange"
                                        variant={plan.badge === "Popular" ? "filled" : "light"}
                                        radius="xl"
                                        w="fit-content"
                                    >
                                        {plan.badge}
                                    </Badge>

                                    <Title order={3} c="white" fw={900}>
                                        {plan.title}
                                    </Title>

                                    <Group align="end" gap={6}>
                                        <Text
                                            c="#f24c1a"
                                            fw={900}
                                            style={{
                                                fontSize: "42px",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {plan.price}
                                        </Text>

                                        <Text c="#aaa" fw={700}>
                                            / plan
                                        </Text>
                                    </Group>

                                    <Text c="#bdbdbd" size="sm" style={{ lineHeight: 1.7 }}>
                                        {plan.desc}
                                    </Text>

                                    <List spacing="sm" c="#ccc" size="sm">
                                        <List.Item>QR-based booking system</List.Item>
                                        <List.Item>Customer document collection</List.Item>
                                        <List.Item>Booking and fleet management</List.Item>
                                        <List.Item>Pre-ride vehicle photo proof</List.Item>
                                    </List>
                                </Stack>
                            </Paper>
                        ))}
                    </SimpleGrid>

                    <Paper
                        mt={28}
                        radius={28}
                        p={{ base: 28, md: 36 }}
                        bg="#1b1b1b"
                        style={{
                            border: "1px solid #f24c1a",
                            boxShadow: "0 20px 70px rgba(242,76,26,0.12)",
                        }}
                    >
                        <Grid align="center" gutter={28}>
                            <Grid.Col span={{ base: 12, md: 8 }}>
                                <Stack gap={12}>
                                    <Badge color="orange" variant="filled" radius="xl" w="fit-content">
                                        {oneTimePlan.badge}
                                    </Badge>

                                    <Title order={3} c="white" fw={900}>
                                        {oneTimePlan.title}
                                    </Title>

                                    <Text c="#bdbdbd" style={{ lineHeight: 1.8 }}>
                                        {oneTimePlan.desc}
                                    </Text>
                                </Stack>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Stack align={{ base: "flex-start", md: "flex-end" }} gap={12}>
                                    <Text
                                        c="#f24c1a"
                                        fw={900}
                                        style={{
                                            fontSize: "46px",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {oneTimePlan.price}
                                    </Text>

                                    <Button
                                        component="a"
                                        href="tel:+918172818877"
                                        radius={14}
                                        size="md"
                                        style={{
                                            backgroundColor: "#f24c1a",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Contact for Offer
                                    </Button>
                                </Stack>
                            </Grid.Col>
                        </Grid>
                    </Paper>
                </Container>
            </Box>
            {/* CTA Section */}
            <Box py={70}>
                <Container size="md">
                    <Paper
                        radius={28}
                        p={{ base: 28, md: 42 }}
                        bg="white"
                        shadow="xs"
                        style={{
                            border: "1px solid #ece7df",
                        }}
                    >
                        <Stack align="center" gap={14}>
                            <Badge color="orange" variant="light">
                                RentalFlow
                            </Badge>

                            <Title order={2} fw={900} c="#1a1a1a" ta="center">
                                Ride More. Earn More.
                            </Title>

                            <Text
                                ta="center"
                                c="#666"
                                maw={650}
                                style={{ lineHeight: 1.8 }}
                            >
                                Give your rental shop a digital booking system
                                that helps you reduce paperwork, verify
                                customers, manage bookings, organize vehicles,
                                and protect your business with photo records.
                            </Text>

                            <Group mt={12} gap={18} justify="center">
                                <Button
                                    component="a"
                                    href="https://instagram.com/rentalflow.in"
                                    target="_blank"
                                    radius={14}
                                    size="md"
                                    style={{
                                        backgroundColor: "#f24c1a",
                                        fontWeight: 700,
                                    }}
                                >
                                    Partner With RentalFlow
                                </Button>

                                <Button
                                    component="a"
                                    href="https://rentalflow.in"
                                    target="_blank"
                                    radius={14}
                                    size="md"
                                    variant="outline"
                                    color="orange"
                                >
                                    Visit rentalflow.in
                                </Button>
                            </Group>

                            <Group mt={12} gap={18} justify="center">
                                <Text
                                    component="a"
                                    href="https://instagram.com/rentalflow.in"
                                    target="_blank"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    Instagram: @rentalflow.in
                                </Text>
                                <Text
                                    component="a"
                                    href="tel:+918172818877"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    Contact: 8172818877
                                </Text>

                                <Text
                                    component="a"
                                    href="https://rentalflow.in"
                                    target="_blank"
                                    fw={700}
                                    c="#f24c1a"
                                    style={{ textDecoration: "none" }}
                                >
                                    rentalflow.in
                                </Text>
                            </Group>

                            <Group mt={6} gap={18} justify="center">
                            <Text
                                component="a"
                                href="/policy"
                                size="sm"
                                c="#777"
                                style={{ textDecoration: "none" }}
                            >
                                Privacy Policy
                            </Text>

                            <Text
                                component="a"
                                href="/terms"
                                size="sm"
                                c="#777"
                                style={{ textDecoration: "none" }}
                            >
                                Terms & Conditions
                            </Text>
                            </Group>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            {/* Footer */}
            <Box py={24}>
                <Container>
                    <Text ta="center" size="sm" c="#777">
                        © {new Date().getFullYear()} RentalFlow · Smart Rentals Made Simple
                    </Text>
                </Container>
            </Box>
        </Box>
    );
}